import React, { useState } from "react";
import { Footer, Header } from "../Components";
import { useTranslation } from "react-i18next";

import ContactUsBanner from "../Components/ContactUsBanner";
import FooterTopBar from "../Components/FooterTopBar";
import axiosClient from "../AxiosClient";
import { checkEmail } from "../Services/EmailValidation";

const inputClass =
  "mt-1 px-3 py-2 bg-white border shadow-sm border-slate-300 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-sky-500 block w-full rounded-md sm:text-sm focus:ring-1";

export const ContactUs = () => {
  document.title = "Contact Us - Famra Cure";
  const { t } = useTranslation();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFieldErrors({ ...fieldErrors, [name]: "" });
    if (name === "email") setEmailSuggestion(null);
  };

  const validate = () => {
    const errors = {};
    if (form.name.trim().length < 2) errors.name = "Please enter your name";
    if (form.message.trim().length < 10) errors.message = "Please write a message of at least 10 characters";

    const { error: emailError, suggestion } = checkEmail(form.email);
    if (emailError) errors.email = emailError;

    setFieldErrors(errors);
    if (Object.keys(errors).length) return false;

    // Ask once about a likely typo in the email domain; sending again keeps the email as typed
    if (suggestion && confirmedEmail !== form.email) {
      setEmailSuggestion(suggestion);
      setConfirmedEmail(form.email);
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    if (!validate()) return;

    try {
      setIsLoading(true);
      const response = await axiosClient.post('/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      const message = response?.data?.message;
      if (response.status === 200) {
        setSuccess(message || "Thank you for contacting us! We will get back to you soon.");
        setForm({ name: "", email: "", message: "" });
        setEmailSuggestion(null);
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const applySuggestion = () => {
    setForm({ ...form, email: emailSuggestion });
    setEmailSuggestion(null);
  };

  return (
    <>
      <Header />
      <ContactUsBanner />
      <div className="my-12 md:my-16 w-[92%] md:w-[83%] mx-auto">
        <div className="w-full max-w-2xl p-6 md:p-8 shadow-xl rounded-md bg-white">
          <h2 className="mb-6 text-2xl md:text-3xl font-bold text-left">
            Send us a message
          </h2>
          <form onSubmit={handleFormSubmit} noValidate>
            <div className="mb-6">
              <label htmlFor="contact-name">{t("contact.nameLabel")}</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={inputClass}
                placeholder={t("contact.namePlaceholder")}
                autoComplete="name"
              />
              {fieldErrors.name && <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>}
            </div>

            <div className="mb-6">
              <label htmlFor="contact-email">{t("contact.emailLabel")}</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={inputClass}
                placeholder="name@gmail.com"
                autoComplete="email"
              />
              {fieldErrors.email && <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>}
              {emailSuggestion && (
                <p className="text-amber-700 text-sm mt-1">
                  Did you mean{" "}
                  <button type="button" onClick={applySuggestion} className="font-semibold underline">
                    {emailSuggestion}
                  </button>
                  ? Press Send again to keep the email as typed.
                </p>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="contact-message">{t("contact.messageLabel")}</label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                className={inputClass}
                placeholder={t("contact.messagePlaceholder")}
                rows="4"
              ></textarea>
              {fieldErrors.message && <p className="text-red-500 text-sm mt-1">{fieldErrors.message}</p>}
            </div>

            <button
              type="submit"
              className={`${isLoading ? 'opacity-40' : 'opacity-100'} w-full rounded bg-[#0d63f3] hover:bg-[#0a4bb4] text-white px-6 py-2.5 text-sm font-medium uppercase transition-colors`}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : t("contact.sendButton")}
            </button>
          </form>
          {(success || error) &&
            <div className={`mt-6 text-center px-4 py-2 rounded-lg
              ${error ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}
            >
              {error ? error : success}
            </div>
          }
        </div>
      </div>

      <FooterTopBar />
      <Footer />
    </>
  );
};

export default ContactUs;
